"use client";
import { AdminProvider } from "@/providers/AdminProvider";
import { useAppContext } from "@/providers/AppProvider";
import { SessionProvider, useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import AdminSidebar from "./admin/AdminSidebar";
import AdminMain from "./admin/Main";
import { LoadingPage } from "./common/Loading";
import Main from "./Main";
import Sidebar from "./Sidebar";

export default function ConditionalLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const { loading, showBanner } = useAppContext();
    const pathname = usePathname();
    const isAdminRoute = pathname.startsWith("/admin");
    const isCaseStudyRoute = pathname.startsWith("/case-studies");

    if (loading) return null;

    if (isCaseStudyRoute) {
        return (
            <div className="fixed inset-0 z-50 h-dvh w-screen overflow-y-auto bg-background-color text-text-color">
                {children}
            </div>
        );
    }

    return isAdminRoute ? (
        <SessionProvider>
            <AdminProvider>
                <AdminLayout>{children}</AdminLayout>
            </AdminProvider>
        </SessionProvider>
    ) : (
        <div
            className={`flex ${showBanner ? "h-[calc(100%-80px)]" : "h-full"} w-full flex-wrap sm:gap-4`}
        >
            <Sidebar />
            <Main>{children}</Main>
        </div>
    );
}

function AdminLayout({ children }: { children: React.ReactNode }) {
    const { status } = useSession();
    const [loading, setLoading] = useState(true);
    const router = useRouter();
    const { theme } = useAppContext();

    useEffect(() => {
        if (status === "unauthenticated") {
            router.push("/admin/login");
            // NextJS Router is slow in navigation so need to add timeout
            setTimeout(() => setLoading(false), 500);
        } else if (status === "authenticated") {
            setLoading(false);
        }
    }, [status, router]);

    if (loading || status === "loading") {
        return <LoadingPage theme={theme} />;
    }

    return status === "unauthenticated" ? (
        <>{children}</>
    ) : (
        <>
            <AdminSidebar />
            <AdminMain>{children}</AdminMain>
        </>
    );
}
